import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { CandidateProfile, LoggableActivityType } from '@nest-recruit/shared';
import { Skeleton, SkeletonLines } from '../../../../components/Skeleton';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../../components/system-states/SystemState';
import { formatNumber } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useTimeline } from '../../../communication/api/communication-api';
import { useCandidatePanelProfile } from '../../api/candidate-panel-api';
import { CrossApplicationBanner } from '../CandidateBits';
import { ActivityTab, loggableOf, type PanelLogKind } from './ActivityTab';
import { DetailsTab } from './DetailsTab';
import { JobsTab } from './JobsTab';
import { PanelAside } from './PanelAside';
import { CvTab, DocumentsTab, InterviewsTab, TestsTab } from './RecordTabs';
import { PANEL_TAB_LABELS, tabCounts } from './panel-format';
import styles from './CandidatePanel.module.css';

// The candidate pop-up and the full profile (CandidatePanel.dc.html, Candidate-profile.dc.html):
// one panel, two frames. The list page opens it over itself; /candidates/{id} wraps it in a page.

export const CANDIDATE_PANEL_TABS = ['cv', 'jobs', 'interviews', 'tests', 'documents', 'activity', 'details'] as const;
export type CandidatePanelTab = (typeof CANDIDATE_PANEL_TABS)[number];

export function isCandidatePanelTab(value: unknown): value is CandidatePanelTab {
  return CANDIDATE_PANEL_TABS.some((tab) => tab === value);
}

/** Previous / Next through the list the pop-up was opened from: "12 of 1,284". */
export interface CandidatePanelNav {
  /** 1-based. */
  readonly position: number;
  readonly total: number;
  /** Null at the first person. */
  readonly onPrevious: (() => void) | null;
  /** Null at the last person. */
  readonly onNext: (() => void) | null;
}

export interface CandidatePanelProps {
  readonly candidateId: string;
  /** `popup` over a list; `page` as the full profile at /candidates/{id}. */
  readonly variant: 'popup' | 'page';
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
  readonly nav?: CandidatePanelNav;
  /** The pop-up's Close; also Esc. */
  readonly onClose?: () => void;
  /** Opened from a list's Email, Call or Note quick action: the Activity tab's log bar starts on this kind. */
  readonly logKind?: 'email' | 'call' | 'note';
}

const DEFAULT_LOG_KIND: PanelLogKind = 'note';
/** ←/→ move along the tabs (06-qol §2). */
const TAB_STEPS: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1 };

/** The panel: who they are on the left, their records in tabs on the right. */
export function CandidatePanel({ candidateId, variant, tab, onTabChange, logKind }: CandidatePanelProps): ReactNode {
  const profile = useCandidatePanelProfile(candidateId);
  const data = profile.data;

  if (!data) {
    if (profile.error?.status === 403) return <ForbiddenState resource="this candidate" contact="Your admin" />;
    if (profile.error?.status === 404) return <NotFoundState />;
    if (profile.error) return <ServerErrorState onRetry={() => void profile.refetch()} />;
    return <PanelSkeleton variant={variant} />;
  }
  // The person on screen stays while the next loads (§5a rule 2); a stale person never takes an action.
  return <LoadedPanel key={data.candidate.id} profile={data} variant={variant} tab={tab} onTabChange={onTabChange} logKind={logKind} isSwapping={profile.isPlaceholderData} />;
}

interface LoadedPanelProps {
  readonly profile: CandidateProfile;
  readonly variant: 'popup' | 'page';
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
  readonly logKind: PanelLogKind | undefined;
  readonly isSwapping: boolean;
}

function LoadedPanel({ profile, variant, tab, onTabChange, logKind, isSwapping }: LoadedPanelProps): ReactNode {
  const isTestsOn = useModule('assessments');
  const [kind, setKind] = useState<LoggableActivityType>(loggableOf(logKind ?? DEFAULT_LOG_KIND));
  const [seenLogKind, setSeenLogKind] = useState(logKind);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const tabsId = useId();
  const tabRefs = useRef(new Map<CandidatePanelTab, HTMLButtonElement>());
  const timeline = useTimeline({ kind: 'candidate', id: profile.candidate.id }, 'all');

  // A quick action from the list (Email, Call, Note) points the log at its kind.
  if (logKind !== seenLogKind) {
    setSeenLogKind(logKind);
    if (logKind) setKind(loggableOf(logKind));
  }

  // #13: Tests belongs to Assessments; with it off the tab is not there and the rest close up.
  const tabs = CANDIDATE_PANEL_TABS.filter((key) => key !== 'tests' || isTestsOn);
  const shownTab: CandidatePanelTab = tabs.includes(tab) ? tab : 'cv';
  const firstPage = timeline.data?.pages[0];
  const counts = tabCounts({
    applications: profile.applications.length,
    interviews: profile.interviews.length,
    tests: profile.tests.length,
    documents: profile.documents.length,
    activity: firstPage ? { count: firstPage.items.length, hasMore: firstPage.hasMore } : null,
  });

  const log = (next: PanelLogKind): void => {
    setKind(loggableOf(next));
    onTabChange('activity');
  };

  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step = TAB_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    if (!next) return;
    onTabChange(next);
    tabRefs.current.get(next)?.focus();
  };

  return (
    <div className={styles.panel} data-variant={variant} aria-busy={isSwapping || undefined}>
      <PanelAside profile={profile} onLog={log} onShowJobs={() => onTabChange('jobs')} isCollapsed={isCollapsed} onToggleCollapsed={() => setIsCollapsed(!isCollapsed)} />
      <section className={styles.records} aria-label="Records">
        <div className={styles.tablist} role="tablist" aria-label="Candidate records">
          {tabs.map((key, index) => (
            <button
              key={key}
              ref={(element) => {
                if (element) tabRefs.current.set(key, element);
                else tabRefs.current.delete(key);
              }}
              type="button"
              role="tab"
              id={`${tabsId}-${key}`}
              aria-selected={key === shownTab}
              aria-controls={`${tabsId}-panel`}
              tabIndex={key === shownTab ? 0 : -1}
              className={styles.tab}
              onClick={() => onTabChange(key)}
              onKeyDown={(event) => onTabKey(event, index)}
            >
              {PANEL_TAB_LABELS[key]}
              {counts[key] !== null && <span className={styles.tabCount}>{counts[key]}</span>}
            </button>
          ))}
        </div>
        <div className={styles.tabPanel} role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${shownTab}`}>
          {profile.liveElsewhere.length > 0 && (
            <div className={styles.banner}>
              <CrossApplicationBanner applications={profile.liveElsewhere} lead="In progress in more than one venture" />
            </div>
          )}
          <TabContent tab={shownTab} profile={profile} kind={kind} onKindChange={setKind} />
        </div>
      </section>
    </div>
  );
}

function TabContent({ tab, profile, kind, onKindChange }: { readonly tab: CandidatePanelTab; readonly profile: CandidateProfile; readonly kind: LoggableActivityType; readonly onKindChange: (kind: LoggableActivityType) => void }): ReactNode {
  switch (tab) {
    case 'cv':
      return <CvTab profile={profile} />;
    case 'jobs':
      return <JobsTab profile={profile} />;
    case 'interviews':
      return <InterviewsTab profile={profile} />;
    case 'tests':
      return <TestsTab profile={profile} />;
    case 'documents':
      return <DocumentsTab profile={profile} />;
    case 'activity':
      return <ActivityTab candidateId={profile.candidate.id} canLog={profile.can.edit} kind={kind} onKindChange={onKindChange} />;
    case 'details':
      return <DetailsTab profile={profile} />;
  }
}

/** First paint only: shaped like the panel, never a page spinner. */
function PanelSkeleton({ variant }: { readonly variant: 'popup' | 'page' }): ReactNode {
  return (
    <div className={styles.panel} data-variant={variant} aria-busy="true">
      <div className={styles.aside}>
        <div className={styles.asideScroll}>
          <Skeleton shape="title" width="60%" />
          <SkeletonLines count={10} />
        </div>
      </div>
      <div className={styles.records}>
        <div className={styles.tabBody}>
          <SkeletonLines count={12} />
        </div>
      </div>
    </div>
  );
}

// --- The pop-up --------------------------------------------------------------------------------

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * The pop-up: the panel in a large dialog over the page that opened it, centred because the owner
 * asked for it (CLAUDE.md §5's exception), full screen on a phone. Previous / Next swap the person
 * in place; Esc closes (after any picker open inside it); focus stays inside and goes back to the
 * row that opened it.
 */
export function CandidatePopup(props: Omit<CandidatePanelProps, 'variant'>): ReactNode {
  const { candidateId, nav, onClose } = props;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      // A picker opened inside (a reason, a tag) closes first, on its own Esc.
      if (document.querySelector('[data-popover]')) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      const isOutside = !(active instanceof Node) || !dialogRef.current?.contains(active);
      if (!event.shiftKey && (active === last || isOutside)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || isOutside)) {
        event.preventDefault();
        last.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      openerRef.current?.focus();
    };
  }, []);

  return createPortal(
    <div
      className={styles.scrim}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div ref={dialogRef} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className={styles.topBar}>
          <span id={titleId} className={styles.topTitle}>
            Candidate
          </span>
          <span className={styles.spacer} />
          {nav && (
            <>
              <button type="button" className={styles.iconButton} aria-label="Previous candidate" disabled={nav.onPrevious === null} onClick={() => nav.onPrevious?.()}>
                <ChevronLeft size={16} aria-hidden />
              </button>
              <span className={styles.position}>
                {formatNumber(nav.position)} of {formatNumber(nav.total)}
              </span>
              <button type="button" className={styles.iconButton} aria-label="Next candidate" disabled={nav.onNext === null} onClick={() => nav.onNext?.()}>
                <ChevronRight size={16} aria-hidden />
              </button>
              <span className={styles.divider} aria-hidden />
            </>
          )}
          <Link to="/candidates/$candidateId" params={{ candidateId }} search={{ tab: props.tab }} className={styles.fullProfile} onClick={() => onClose?.()}>
            <Maximize2 size={14} aria-hidden />
            Full profile
          </Link>
          <button type="button" className={styles.iconButton} aria-label="Close" onClick={() => onClose?.()}>
            <X size={18} aria-hidden />
          </button>
        </header>
        <CandidatePanel {...props} variant="popup" />
      </div>
    </div>,
    document.body,
  );
}
